import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';

import { fireErrorAnalytics, fireSiteOptedOutAnalytics } from '../../services/analytics/index.tsx';
import { getOrUpdateFeatureVisited } from '../../services/caching/index.tsx';
import { checkJpdCrossJoinNudgeEligibility } from '../../services/jira-jpd/eligibility.tsx';
import type { EligibilityCheck } from './types.tsx';

export const useJpdCrossJoinNudgeEligibility = () => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const licensedProducts = useLicensedProducts();
	const isJpdRoute = useIsActiveRoute('polaris-ideas');

	return useCallback(async (): Promise<EligibilityCheck> => {
		try {
			let snoozeFor = 30;
			if (fg('twcg-1201-cjn-pacing-eligibility-calibration')) {
				snoozeFor = 7;
			}
			if (getOrUpdateFeatureVisited('crossJoinNudgesJiraToJpd', snoozeFor)) {
				return { isEligible: false, ineligibleReason: 'feature visited too recently' };
			}

			if (isJpdRoute) {
				return { isEligible: false, ineligibleReason: 'jpd route' };
			}

			const eligibilityResponse = await checkJpdCrossJoinNudgeEligibility({
				cloudId,
				userId,
				licensedProducts,
			});
			if (!eligibilityResponse.eligible) {
				if (eligibilityResponse.ineligibilityReason === 'SITE_OPTED_OUT') {
					fireSiteOptedOutAnalytics(createAnalyticsEvent, 'crossJoinNudgesJiraToJpd');
				}
				return { isEligible: false, ineligibleReason: eligibilityResponse.ineligibilityReason };
			}
			return { isEligible: true, data: {} };

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('crossJoinNudgesJiraToJpd', error, { cloudId });
			return { isEligible: false, ineligibleReason: 'unexpected error' };
		}
	}, [cloudId, userId, createAnalyticsEvent, isJpdRoute, licensedProducts]);
};
